/**
 * AgentUsageView — one Agent's drill on the Usage report
 * (/usage/agents/:agentId?from&to).
 *
 * What the Agent was charged over the range beside the same tokens at Claude
 * list on three bases (all Sonnet, the matched tier, all Opus), the four bars
 * on one scale with Claude's prompt writing as its own segment, the largest
 * runs (for Proctor, the largest reviews with their pull request), and how
 * the Claude side is priced. ADR-0028: sell side only, admin-gated
 * server-side; the route allowlists the Agent.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import type { YagniCodeAgentRunRow, YagniCodeAgentRunsResponse } from "@yagni/shared";

import { ListHead } from "../queue/index.js";
import { Note, Sec, Seg } from "./usageParts.js";
import {
  FRONTIER_BASES,
  UnknownAgentError,
  UsageAccessError,
  agentLabel,
  agentRunNoun,
  agentRunsCsvHref,
  basisSavingsPercent,
  defaultBasisFor,
  fetchAgentRuns,
  formatUsd,
  rangeFromSearch,
  rangeLabel,
  teamDrillPath,
  teamLabel,
  type AgentRunsPage,
  type FrontierBasisKey,
  type UsageRange,
} from "./usageReport.js";
import {
  ClaudeMethodNote,
  ComparisonBars,
  GlassCard,
  GlassStat,
  GlassTiles,
  SAVE_COLOR,
  savedWithShare,
} from "./usageGlass.js";
import { UsageBreadcrumb, UsageDrillGate } from "./usageDrill.js";

export type FetchAgentRuns = (
  agentId: string,
  range: UsageRange,
  page: AgentRunsPage,
) => Promise<YagniCodeAgentRunsResponse>;

const PAGE: AgentRunsPage = { sort: "charged", limit: 50, offset: 0 };

const mono = "font-[family-name:var(--font-mono)]";
const TH = "px-4 py-3 font-semibold text-[var(--text-secondary)]";

export function AgentUsageRoute() {
  return (
    <UsageDrillGate>
      <AgentUsageView />
    </UsageDrillGate>
  );
}

export function AgentUsageView({ fetchRuns = fetchAgentRuns }: { fetchRuns?: FetchAgentRuns }) {
  const { agentId = "" } = useParams<{ agentId: string }>();
  const [search] = useSearchParams();
  const fromParam = search.get("from");
  const toParam = search.get("to");
  // Keyed on the two strings so a new Date each render does not refetch.
  const range = useMemo(
    () => rangeFromSearch(new URLSearchParams({ from: fromParam ?? "", to: toParam ?? "" }), new Date()),
    [fromParam, toParam],
  );
  const [basis, setBasis] = useState<FrontierBasisKey>(() => defaultBasisFor(agentId));
  const [data, setData] = useState<YagniCodeAgentRunsResponse | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "error" | "forbidden" | "unknown">(
    "loading",
  );
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setBasis(defaultBasisFor(agentId));
  }, [agentId]);

  useEffect(() => {
    let cancelled = false;
    setState("loading");
    setError(null);
    fetchRuns(agentId, range, PAGE)
      .then((loaded) => {
        if (cancelled) return;
        setData(loaded);
        setState("ready");
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof UsageAccessError) setState("forbidden");
        else if (err instanceof UnknownAgentError) setState("unknown");
        else {
          setError(err instanceof Error ? err.message : "Failed to load Agent usage");
          setState("error");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [fetchRuns, agentId, range]);

  const noun = agentRunNoun(agentId);
  const isProctor = agentId === "code_review";

  if (state === "loading") return <Note>Reading the Agent's runs</Note>;
  if (state === "forbidden") {
    return (
      <div>
        <UsageBreadcrumb trail={["Agents"]} />
        <Note tone="honest">Agent usage is visible to workspace admins.</Note>
      </div>
    );
  }
  if (state === "unknown") {
    return (
      <div>
        <UsageBreadcrumb trail={["Agents"]} />
        <Note tone="honest">There is no Agent with that name on the Usage report.</Note>
      </div>
    );
  }
  if (state === "error" || !data) {
    return (
      <div>
        <UsageBreadcrumb trail={["Agents"]} />
        <Note tone="honest">{error ?? "Failed to load Agent usage."}</Note>
      </div>
    );
  }

  const name = agentLabel(data);
  const selected = data.frontier[basis];
  const selectedLabel = FRONTIER_BASES.find((entry) => entry.key === basis)?.label ?? "";
  const percent = basisSavingsPercent(selected);

  return (
    <div data-testid="agent-usage" style={{ display: "contents" }}>
      <UsageBreadcrumb trail={["Agents", name]} />
      <ListHead
        title={name}
        sub={["Agent", `${rangeLabel(range)}. What ${name} was charged, beside the same tokens at Claude list.`]}
        acts={
          <a href={agentRunsCsvHref(agentId, range)} download className="btn secondary sm">
            Download CSV
          </a>
        }
        chips={
          <Seg
            label="Compare against"
            options={FRONTIER_BASES}
            value={basis}
            onChange={setBasis}
          />
        }
      />

      <GlassTiles testId="agent-tiles">
        <GlassStat
          label="Charged"
          value={formatUsd(data.totalSellMillicents)}
          sub={`${data.totalDispatches.toLocaleString("en-US")} dispatches`}
        />
        <GlassStat label="Claude cost" value={formatUsd(selected.totalMillicents)} sub={selectedLabel} />
        <GlassStat
          label="Saved"
          value={savedWithShare(selected.savingsMillicents, percent)}
          sub={`vs ${selectedLabel}`}
          tone="save"
        />
        <GlassStat
          label="Claude prompt writing"
          value={formatUsd(selected.promptWritingMillicents)}
          sub="included in Claude cost"
        />
      </GlassTiles>
      {data.frontier.counterfactualIncomplete && (
        <p className="fact">
          Some of {name}'s spend ran on a lane with no Claude list equivalent, so the Claude figures
          cover part of it rather than guess.
        </p>
      )}

      <Sec title="Against Claude" fact="same tokens, list price on the day">
      <GlassCard>
        <ComparisonBars
          bars={[
            { key: "yagni", label: "YAGNI", value: { kind: "yagni", millicents: data.totalSellMillicents } },
            {
              key: "matched",
              label: "Claude, matched tier",
              value: { kind: "claude", basis: data.frontier.matched },
              selected: basis === "matched",
            },
            {
              key: "standard",
              label: "Claude Sonnet",
              value: { kind: "claude", basis: data.frontier.standard },
              selected: basis === "standard",
            },
            {
              key: "advanced",
              label: "Claude Opus",
              value: { kind: "claude", basis: data.frontier.advanced },
              selected: basis === "advanced",
            },
          ]}
        />
      </GlassCard>
      </Sec>

      <Sec
        title={`Largest ${noun.many}`}
        fact={
          data.total > data.runs.length
            ? `${data.runs.length} of ${data.total.toLocaleString("en-US")}, every one in the CSV`
            : `${data.total.toLocaleString("en-US")} in range`
        }
      >
      {data.runs.length === 0 ? (
        <GlassCard>
          <p className="m-0 text-sm text-[var(--text-tertiary)]">
            No {noun.many} by {name} in this range.
          </p>
        </GlassCard>
      ) : (
        <div className="tbl usage-tbl" data-testid="agent-runs">
          <table>
            <thead>
              <tr className="border-b border-[var(--glass-border)] text-left">
                <th scope="col" className={TH}>
                  Started
                </th>
                <th scope="col" className={TH}>
                  Team
                </th>
                <th scope="col" className={TH}>
                  {isProctor ? "Pull request" : capitalize(noun.one)}
                </th>
                <th scope="col" className={`${TH} num`}>
                  Charged
                </th>
                <th scope="col" className={`${TH} num`}>
                  Claude
                </th>
                <th scope="col" className={`${TH} num`}>
                  Saved
                </th>
                <th scope="col" className={`${TH} num`}>
                  Prompt writing
                </th>
              </tr>
            </thead>
            <tbody>
              {data.runs.map((row, index) => (
                <RunRow key={row.runId ?? `outside-${row.deskId ?? "none"}-${index}`} row={row} basis={basis} range={range} />
              ))}
            </tbody>
          </table>
        </div>
      )}
      </Sec>

      <ClaudeMethodNote />
    </div>
  );
}

function capitalize(word: string): string {
  return word.charAt(0).toUpperCase() + word.slice(1);
}

function RunRow({
  row,
  basis,
  range,
}: {
  row: YagniCodeAgentRunRow;
  basis: FrontierBasisKey;
  range: UsageRange;
}) {
  const claude = row.frontier[basis];
  return (
    <tr className="border-b border-[var(--glass-border)] last:border-b-0" data-testid="agent-run-row">
      <td className={`px-4 py-3 ${mono} text-[var(--text-secondary)]`}>
        {row.startedAt ? row.startedAt.slice(0, 10) : "-"}
      </td>
      <td className="px-4 py-3">
        <Link
          to={teamDrillPath(row.deskId, range)}
          className="text-[var(--text-primary)] no-underline hover:underline"
        >
          {teamLabel(row)}
        </Link>
      </td>
      <td className="px-4 py-3">
        {row.link ? (
          <a
            href={row.link.url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[var(--accent)] no-underline hover:underline"
          >
            {row.link.label ?? row.title ?? "Open"}
          </a>
        ) : row.runId === null ? (
          <span className="italic text-[var(--text-tertiary)]">Outside a run</span>
        ) : (
          <span className="text-[var(--text-secondary)]">{row.title ?? "Untitled"}</span>
        )}
        {row.link && row.title && row.link.label ? (
          <div className="text-xs text-[var(--text-tertiary)]">{row.title}</div>
        ) : null}
      </td>
      <td className={`px-4 py-3 text-right ${mono} font-semibold`}>{formatUsd(row.sellMillicents)}</td>
      <td className={`px-4 py-3 text-right ${mono} text-[var(--text-secondary)]`}>
        {formatUsd(claude.totalMillicents)}
      </td>
      <td className={`px-4 py-3 text-right ${mono}`} style={{ color: SAVE_COLOR }}>
        {formatUsd(claude.savingsMillicents)}
      </td>
      <td className={`px-4 py-3 text-right ${mono} text-[var(--text-tertiary)]`}>
        {formatUsd(claude.promptWritingMillicents)}
      </td>
    </tr>
  );
}
